import React, {useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import {MAX_IMG_SIZE} from '../../../../../constants';

import './imageSelectCompact.scss';
import Label from '../../../label/Label';

export type ImageSelectCompactValue = {image: string; name: string};

interface ImageSelectCompactProps {
    image: ImageSelectCompactValue;
    label: string;
    onChange: (image: ImageSelectCompactValue | null) => void;
    onUpload: (file: File) => Promise<{url: string}>;
}

function ImageSelectCompact({image, label, onChange, onUpload}: ImageSelectCompactProps): JSX.Element {
    const [isChecked, setIsChecked] = useState(!!image.image);

    const {t} = useTranslation();
    const imageInputRef = useRef<null | HTMLInputElement>(null);

    useEffect(() => {
        setIsChecked(!!image.image);
    }, [image.image]);

    const handleCheckboxClick = (): void => {
        if (isChecked) {
            onChange(null);
        }
        setIsChecked(!isChecked);
    };

    const handleClick = (): void => {
        imageInputRef.current && imageInputRef.current.click();
    };

    return (
        <div className={`c-imageSelectCompact ${isChecked ? 'c-imageSelectCompact--checked' : ''}`}>
            <div className="c-imageSelectCompact__input" onClick={handleCheckboxClick}>
                <Icon className="c-imageSelectCompact__input_checkbox" icon={isChecked ? 'solar:check-square-bold' : 'solar:stop-linear'} />
            </div>
            <div className="c-imageSelectCompact__select" onClick={handleClick}>
                {image.image && <img src={image.image} alt="" className="c-imageSelectCompact__select_image" />}
                {!image.image && <hr className="c-imageSelectCompact__select_noImageLine" />}
                <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    className="u-d-none"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (!e.target.files) return;
                        const file = e.target.files[0];
                        if (file.size > MAX_IMG_SIZE) {
                            toast.warning(t('presEditPage.subNavBar.bgImgTooBig'), {toastId: 'bgImgTooBig'});
                            e.target.value = '';
                            return;
                        }

                        onUpload(file)
                            .then(({url}) => {
                                onChange({image: url, name: file.name});
                                setIsChecked(true);
                                e.target.value = '';
                            })
                            .catch(() => {
                                toast.warning(t('toasts.bgImgFail'), {toastId: 'bgImgFail'});
                                e.target.value = '';
                            });
                    }}
                />
            </div>
            <Label>{label}</Label>
        </div>
    );
}

export default ImageSelectCompact;
